import { getRouteApi } from '@tanstack/react-router';
import { Bell, CircleCheck, CircleDollarSign, CircleX, Fuel, Hourglass, Info, Pencil, TriangleAlert } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { BarraFiltros, type ChipFiltro } from '@/componentes/BarraFiltros';
import { Botao } from '@/componentes/Botao';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { ChaveDeFiltro } from '@/componentes/ChaveDeFiltro';
import { Barra } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Medida } from '@/componentes/Medida';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarAteDuasCasas, formatarData, formatarDataHora, formatarDecimal, formatarHora, formatarInteiro, formatarReais, interpretarDataHora, paraCampoDataHora } from '@/lib/formato';
import { combinaComTermo } from '@/lib/texto';

import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';

import { JanelasDoAbastecimento } from './EstornoDoAbastecimento';
import { MenuDaLinhaDoAbastecimento, type AcaoNaLinhaDoAbastecimento } from './MenuDoAbastecimento';
import {
  CAMPOS_DA_BUSCA,
  OPCOES_POR_PAGINA,
  PADRAO_POR_PAGINA,
  PENDENCIAS,
  ehFiltroDePendencia,
  montarBusca,
  type FiltroDePendencia,
} from './busca';
import { useAbastecimentos, useCadastros, useConferirAbastecimento, useOrdens } from './consultas';
import { juntarNaPosicaoOriginal, textoDaCorrecaoDoAbastecimento } from './estorno';
import {
  calcularLancamentos,
  resumoDoPeriodo,
  textoDaFaixa,
  ultimoRegistroDeHorimetro,
  vizinhosDe,
  type Lancamento,
  type ResumoDoPeriodo,
} from './consumo';
import { gastoDosAbastecimentos, totalDoAbastecimento } from './tipos';
import type { Abastecimento, EquipamentoDoModulo } from './tipos';
import { FormularioAbastecimento } from './FormularioAbastecimento';
import { HubDoCombustivel } from './HubDoCombustivel';
import { LancamentoRapido } from './LancamentoRapido';
import { litrosEscritos } from './litros';
import {
  ROTULO_DA_VERIFICACAO,
  estadoDaVerificacao,
  faixaJulgaOConsumo,
  origemDaLinha,
  paraConferir,
  posicaoNaFaixa,
  verificacoesDoLancamento,
  type EstadoDaVerificacao,
  type NivelDaVerificacao,
  type Verificacao,
} from './verificacao';

const rota = getRouteApi('/combustivel/lancamento');

/**
 * Lançamento de abastecimento — a tela que alimenta o módulo 08.
 *
 * DUAS PARTES, E A ORDEM IMPORTA
 * Primeiro alimentar, depois visualizar. Quem abre esta tela às 6h40 da manhã
 * abre para LANÇAR — o formulário é a primeira coisa abaixo do aviso, não os
 * indicadores. O gestor que abre às 8h para perguntar "quanto gastamos" rola
 * uma tela e acha os números. O contrário custaria uma rolagem a quem
 * usa a tela trinta vezes por dia para economizar uma de quem usa uma vez.
 *
 * O SEGUNDO CAMINHO É A FOLHA DE CAMPO
 * A folha impressa lida pela IA (0035; decisões 9, 10 e 12) é o caminho do
 * campo para o sistema. O cartão junto do formulário leva até ela.
 *
 * O CONSUMO DA TABELA É O DO BANCO
 * `comb.consumo_abastecimento` já compara dois abastecimentos do mesmo ativo,
 * com o mesmo tipo de medidor, e declara a limitação quando o número não é
 * confiável. A tela mostra a limitação em vez de esconder a linha: esconder
 * faria a média parecer melhor do que é.
 */

/* Cada linha de texto em `TextoDaCelula` com uma linha só (p.18: 48 px
   quando há duas informações): o texto que não cabe sai com reticências e
   inteiro no `title` — e inteiro no cartão, abaixo de 768. Código, data com
   hora e número não quebram. */
/**
 * Uma linha da lista (R06, p.69): o lançamento que vale ou o estornado que a
 * chave "Mostrar estornados" trouxe de volta à posição original. O corrigido
 * traz a mudança escrita e o motivo.
 */
type LinhaDoLancamento = Lancamento & {
  readonly estornado?: boolean;
  readonly correcao?: string | null;
  readonly motivoDaCorrecao?: string | null;
};

/** Toda soma da lista é só do que vale: o estornado aparece, e não soma. */
const valem = (linhas: readonly LinhaDoLancamento[]) => linhas.filter((l) => !l.estornado);
const TRACO_APAGADO = <span className="numero block text-tinta-apagada">—</span>;

const COLUNAS: readonly Coluna<LinhaDoLancamento>[] = [
  {
    chave: 'equipamento',
    rotulo: 'Equipamento',
    celula: ({ abastecimento, tipo, estornado, correcao }) =>
      estornado ? (
        <>
          <span className="codigo block text-tinta-apagada line-through">{abastecimento.equipamentoCodigo}</span>
          <TextoDaCelula texto={`Estorno: ${abastecimento.estorno?.motivo ?? 'sem motivo registrado'}`} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </>
      ) : (
        <>
          <span className="codigo block text-tinta">{abastecimento.equipamentoCodigo}</span>
          {/* O corrigido (p.69): a mudança na segunda linha, no lugar do tipo. */}
          <TextoDaCelula texto={correcao ?? tipo ?? 'Fora da frota'} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </>
      ),
  },
  {
    chave: 'quando',
    rotulo: 'Quando',
    celula: ({ abastecimento, estornado, correcao, motivoDaCorrecao }) => (
      <>
        <span className={`txt-corpo block whitespace-nowrap ${estornado ? 'text-tinta-apagada' : 'text-tinta'}`}>{formatarDataHora(abastecimento.quando)}</span>
        {correcao ? (
          /* CORRIGIDO (R06, p.69): a etiqueta âmbar; o motivo abre ao passar o mouse. */
          <span
            title={`Motivo: ${motivoDaCorrecao ?? 'não registrado'}`}
            /* `relative` pelo mesmo motivo da etiqueta de verificação: o
               `sr-only` abaixo é absoluto, e sem pai posicionado ele escapa do
               rolador e estica o documento. Esta etiqueta só aparece na linha
               corrigida, e por isso o defeito aqui ficava dormindo. */
            className="relative inline-flex h-6 items-center gap-1 rounded-etiqueta bg-atencao-fundo px-2 txt-legenda text-atencao-texto"
          >
            <Pencil aria-hidden="true" className="size-3 shrink-0" />
            Corrigido
            <span className="sr-only">{`. Motivo: ${motivoDaCorrecao ?? 'não registrado'}`}</span>
          </span>
        ) : (
          <TextoDaCelula texto={origemDoLancamento(abastecimento)} classe="txt-apoio text-tinta-apagada" linhas={1} />
        )}
      </>
    ),
  },
  /* PRODUTO, e não só "litros": a mesma máquina recebe diesel e ARLA no mesmo
     dia, e as duas linhas ficavam idênticas na lista — 170 L de quê? O ARLA
     também é o produto que não tem consumo por hora, e ler a coluna ao lado do
     traço do consumo explica o traço. */
  {
    chave: 'produto',
    rotulo: 'Produto',
    celula: ({ abastecimento, estornado }) => (
      <TextoDaCelula
        texto={abastecimento.produto ?? 'Sem produto'}
        classe={`txt-corpo ${estornado ? 'text-tinta-apagada' : abastecimento.produto ? 'text-tinta' : 'text-tinta-secundaria'}`}
        linhas={1}
      />
    ),
  },
  {
    chave: 'litros',
    rotulo: 'Litros',
    alinhamento: 'numero',
    celula: ({ abastecimento, estornado }) =>
      estornado ? (
        <span className="numero text-tinta-apagada line-through">{`${litrosEscritos(abastecimento.litros)} L`}</span>
      ) : (
        <Medida valor={litrosEscritos(abastecimento.litros)} unidade="L" />
      ),
    total: (linhas) => (
      <Medida valor={litrosEscritos(valem(linhas).reduce((soma, l) => soma + l.abastecimento.litros, 0))} unidade="L" />
    ),
  },
  /* O MEDIDOR LANÇADO, ao lado dos litros. É o par que se confere: um zero a
     mais nos litros e um dígito trocado no horímetro produzem o mesmo desvio
     de consumo, e sem ver os dois números não há como saber qual deles é o
     errado. A unidade é a do medidor do lançamento — caminhão em km. */
  {
    chave: 'medidor',
    rotulo: 'Medidor',
    alinhamento: 'numero',
    celula: ({ abastecimento, unidade, estornado }) => {
      if (estornado) return TRACO_APAGADO;
      if (abastecimento.horimetro === null) {
        return (
          <span className="block">
            <span className="numero block text-tinta-secundaria">—</span>
            <span className="txt-linha2 block text-tinta-secundaria">sem leitura</span>
          </span>
        );
      }
      return <Medida valor={formatarAteDuasCasas(abastecimento.horimetro)} unidade={unidade === 'L/km' ? 'km' : 'h'} />;
    },
  },
  {
    chave: 'consumo',
    rotulo: 'Consumo',
    alinhamento: 'numero',
    /* Quando não há consumo, a célula diz POR QUE, em duas linhas como as
       outras: o traço no lugar do número e o motivo embaixo, em 12 px. A
       frase do banco inteira ("sem leitura no abastecimento anterior") numa
       célula que não quebra ocupava 271 px de uma coluna de número e
       empurrava Centro de custo e Pendência para fora já em 1440. Ela vai
       inteira no `title`; na célula, a forma curta de cada motivo
       (`motivoCurto`).
       A unidade é a do MEDIDOR do lançamento: caminhão em L/km, máquina em
       L/h. Fixa em "L/h", ela escrevia o caminhão em litros por hora, e a aba
       Consumo mostrava o mesmo número em L/km. */
    celula: ({ consumo, abastecimento, unidade, estornado }) =>
      estornado ? (
        TRACO_APAGADO
      ) : consumo === null ? (
        <span className="block" title={abastecimento.limitacao ?? undefined}>
          <span className="numero block text-tinta-secundaria">—</span>
          <span className="txt-linha2 block text-tinta-secundaria">{motivoCurto(abastecimento.limitacao)}</span>
        </span>
      ) : (
        <Medida valor={formatarDecimal(consumo, 1)} unidade={unidade} />
      ),
  },
  {
    chave: 'custo',
    rotulo: 'Custo',
    alinhamento: 'numero',
    prioridade: 'secundaria',
    celula: ({ abastecimento, estornado }) => {
      if (estornado) return TRACO_APAGADO;
      const total = totalDoAbastecimento(abastecimento);
      return total === null ? (
        <span className="numero text-tinta-secundaria">Sem preço</span>
      ) : (
        <span className="text-tinta">{formatarReais(total)}</span>
      );
    },
    total: (linhas) => (
      <span className="text-tinta">{formatarReais(somaDosCustos(valem(linhas)))}</span>
    ),
  },
  /* O CUSTO É SECUNDÁRIO (R01, p.28): sai abaixo de 1280 e dos cartões. Em M
     ficam as colunas que conferem o lançamento — máquina, quando, produto,
     litros, medidor, consumo e verificação. Quem abre esta tela para ver gasto
     tem o "R$ … de gasto" do hub e a aba de cobranças; quem a abre para
     conferir precisa dos sete.
     OPERADOR, CENTRO DE CUSTO E OBRA saíram das colunas e foram para o painel
     de detalhe (p.20: no máximo oito colunas visíveis, o resto no painel).
     A busca acima da tabela continua alcançando os três. */
  /* A VERIFICAÇÃO (0122), no lugar da coluna "Pendência".
     Não é troca de rótulo: a pendência dizia o que falta e parava ali — o
     desvio de faixa ficava aceso para sempre, porque não havia como alguém
     dizer "olhei, e está certo". O selo tem três estados e o terceiro é a
     assinatura: `Conferido`, `Conferir consumo`, `Verificado`.
     É SELO PREENCHIDO (R02, p.32), com rótulo escrito — nunca só a cor. As
     outras pendências do lançamento (sem operador, sem centro de custo) não
     entram aqui: elas não se resolvem olhando, e sim corrigindo o lançamento,
     e estão na lista de verificações do painel. */
  {
    chave: 'verificacao',
    rotulo: 'Verificação',
    celula: (linha) => {
      const { abastecimento, estornado } = linha;
      if (estornado) {
        /* O estornado não se verifica: o selo diz o que ele é, escrito. */
        return (
          <span
            title={abastecimento.estorno ? `${abastecimento.estorno.por ?? 'Alguém'} estornou em ${formatarDataHora(abastecimento.estorno.em)}` : undefined}
            className="inline-flex h-[var(--selo-altura)] items-center gap-1.5 rounded-pill bg-nao-alocado-fundo px-2 text-nao-alocado-texto"
          >
            <span aria-hidden="true" className="size-[var(--selo-ponto)] rounded-pill bg-nao-alocado" />
            <span className="txt-selo">Estornado</span>
          </span>
        );
      }
      const estado = estadoDaVerificacao(linha);
      return (
        <>
          <SeloDaVerificacao estado={estado} />
          {abastecimento.conferencia?.por ? (
            <TextoDaCelula texto={`por ${abastecimento.conferencia.por}`} classe="txt-apoio text-tinta-apagada" linhas={1} />
          ) : null}
        </>
      );
    },
  },
];

/** As três tintas do selo de verificação, cada uma com o seu significado. */
const TOM_DA_VERIFICACAO: Readonly<Record<EstadoDaVerificacao, { readonly fundo: string; readonly ponto: string; readonly texto: string }>> = {
  /* Verde: alguém olhou e assinou. */
  conferido: { fundo: 'bg-sucesso-fundo', ponto: 'bg-sucesso', texto: 'text-sucesso-texto' },
  /* Âmbar, nunca vermelho: o lançamento ENTROU e o número pode estar certo —
     a máquina trabalhou em areia e o consumo subiu mesmo. Vermelho diria que
     há um erro, e quem o lê todos os dias para de ler. */
  conferir: { fundo: 'bg-atencao-fundo', ponto: 'bg-atencao', texto: 'text-atencao-texto' },
  /* Neutro: o sistema conferiu o que sabe conferir e não achou nada. Verde
     aqui competiria com a assinatura de quem olhou de verdade. */
  verificado: { fundo: 'bg-nao-alocado-fundo', ponto: 'bg-nao-alocado', texto: 'text-nao-alocado-texto' },
};

function SeloDaVerificacao({ estado }: { readonly estado: EstadoDaVerificacao }) {
  const tom = TOM_DA_VERIFICACAO[estado];
  return (
    <span className={`inline-flex h-[var(--selo-altura)] items-center gap-1.5 rounded-pill px-2 ${tom.fundo} ${tom.texto}`}>
      <span aria-hidden="true" className={`size-[var(--selo-ponto)] rounded-pill ${tom.ponto}`} />
      <span className="txt-selo">{ROTULO_DA_VERIFICACAO[estado]}</span>
    </span>
  );
}

/* A forma curta de cada limitação que `comb.consumo_abastecimento` declara
   (migração 0011), com a palavra que o módulo já usa: "sem base" é o "Sem
   base de comparação" da aba Consumo. Curta porque é ela que decide a largura
   da coluna — com "sem leitura anterior" as oito colunas pediam 997 px em L,
   onde a área útil tem 966. A frase inteira do banco fica no `title` da
   célula, e a que o mapa não conhecer sai como "sem consumo", sem inventar
   motivo. */
const MOTIVO_CURTO: Readonly<Record<string, string>> = {
  'sem leitura neste abastecimento': 'sem leitura',
  'sem leitura no abastecimento anterior': 'sem base',
  'medidor nao andou entre os dois': 'medidor parado',
};

function motivoCurto(limitacao: string | null | undefined): string {
  if (limitacao === null || limitacao === undefined) return 'sem base';
  return MOTIVO_CURTO[limitacao] ?? 'sem consumo';
}

/* "Posto · Posto Trevo — Anápolis" repetia a palavra e empurrava a segunda
   linha para a terceira. O prefixo só entra quando o nome não a diz: é ele
   que separa o externo do tanque próprio na conferência. */
function origemDoLancamento(a: Abastecimento): string {
  if (a.posto) return /^posto\b/i.test(a.posto) ? a.posto : `Posto · ${a.posto}`;
  if (a.tanque) return a.tanque;
  return 'Origem não informada';
}

function somaDosCustos(linhas: readonly Lancamento[]): number {
  return linhas.reduce((soma, l) => soma + (totalDoAbastecimento(l.abastecimento) ?? 0), 0);
}

export function PaginaLancamento() {
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();

  const consultaAbastecimentos = useAbastecimentos();
  const consultaCadastros = useCadastros();

  /* 0087 · estornar e corrigir, pelos três pontos da linha. */
  const sessaoAtual = useSessaoEmMemoria();
  const permissoes = sessaoAtual?.permissoes ?? null;
  /* Quem nao lanca nao recebe o formulario (prova do marco, 07/10/2026): ele
     enchia o formulario inteiro e so o envio dizia que faltava a permissao. */
  const podeLancar = permissoes === null || permissoes.includes('abastecimento.registrar');
  /* A ação aberta nos três pontos (ou no "Corrigir lançamento" do painel). O
     menu e as janelas são os da lista principal (`MenuDoAbastecimento.tsx`). */
  const [acaoNaLinha, setAcaoNaLinha] = useState<AcaoNaLinhaDoAbastecimento | null>(null);
  /* 0122 · conferir é OUTRA permissão, e não a de lançar: quem lança não é
     quem confere. Sem sessão em memória (`null`) a tela não decide — o
     servidor decide, e a recusa dele diz qual permissão falta. */
  const podeConferir = permissoes === null || permissoes.includes('abastecimento.conferir');
  /* A linha aberta no painel de detalhe. Guarda o ID, e não a linha: a lista
     chega outra vez a cada invalidação, e um objeto guardado ficaria sendo a
     versão de antes da conferência. */
  const [linhaAberta, setLinhaAberta] = useState<string | null>(null);
  /*
   * O CARTAO DE LANCAR TEM DOIS MODOS, e um lugar so.
   *
   * `rapido` e a linha de quatro perguntas da entrega do dono: maquina,
   * produto, litros e medidor, com as verificacoes mudando a cada tecla. Da
   * conta do diesel que sai do comboio, que e o lancamento de todo dia.
   *
   * `completo` e o formulario inteiro, no MESMO cartao: tipo, posto com cupom
   * e preco, data retroativa, ordem, operador, responsavel, obra e centro de
   * custo. Trocar de modo leva a maquina junto — quem ja a escolheu nao a
   * escolhe de novo.
   *
   * Nunca os dois ao mesmo tempo: dois lancadores na mesma tela sao duas
   * respostas para a mesma pergunta, e a pessoa escolhe a errada na pressa.
   *
   * ## O PADRAO VOLTOU A SER O COMPLETO (27/10/2026)
   *
   * O dono, depois de usar: *"ha dois tipos de abastecimentos, dois
   * formularios... aquele trem de formulario simples tambem nao colou. Nao
   * adianta, aquele layout la, e voltar para o normal mesmo. Do computador"*.
   *
   * Isto REVERTE o padrao de 25/10, que era dele tambem — e a reversao fica
   * escrita porque a razao mudou com o uso, nao com a opiniao: na mesa quem
   * lanca costuma estar lancando o que ja aconteceu (a folha que chegou da
   * obra, o cupom da semana), e isso pede data retroativa, obra, responsavel e
   * tipo — tudo que so o completo tem. A linha rapida foi desenhada para o
   * tablet do comboio, que lanca o que esta acontecendo agora.
   *
   * O rapido NAO SAIU: ele continua a um toque, no mesmo cartao. O que mudou e
   * qual dos dois abre primeiro. O CELULAR NAO MUDA — o dono disse que ainda
   * nao validou, e mexer no que nao foi olhado seria trocar um problema
   * conhecido por um que ninguem viu.
   */
  const [modoDeLancar, setModoDeLancar] = useState<'rapido' | 'completo'>('completo');
  const [codigoEscolhido, setCodigoEscolhido] = useState('');
  /* A data e hora mora na PAGINA: e ela que decide qual abastecimento e o
     "anterior", e e contra ele que o consumo e o piso do medidor se medem. */
  const [quandoEscolhido, setQuandoEscolhido] = useState(() => paraCampoDataHora(new Date()));
  const estornados = useMemo(() => consultaAbastecimentos.data?.estornados ?? [], [consultaAbastecimentos.data]);
  /* R06 (p.69): o estornado fica oculto atrás da chave "Mostrar estornados". */
  const [mostrarEstornados, setMostrarEstornados] = useState(false);

  const termo = busca.termo ?? '';
  const pendencia = busca.pendencia;
  const pagina = busca.pagina ?? 1;
  const porPagina = busca.porPagina ?? PADRAO_POR_PAGINA;

  const abastecimentos = useMemo(
    () => consultaAbastecimentos.data?.linhas ?? [],
    [consultaAbastecimentos.data],
  );
  const cadastros = consultaCadastros.data ?? null;
  const equipamentos = useMemo<readonly EquipamentoDoModulo[]>(
    () => cadastros?.equipamentos ?? [],
    [cadastros],
  );


  const lancamentos = useMemo(
    () => calcularLancamentos(abastecimentos, equipamentos),
    [abastecimentos, equipamentos],
  );

  const resumo = useMemo(() => resumoDoPeriodo(lancamentos), [lancamentos]);
  /* 0122 · o número do indicador "Para conferir", sobre a lista INTEIRA do
     período e não sobre a página nem sobre o filtro: um indicador que muda
     quando se filtra a lista não é indicador, é contagem da tela. */
  const esperandoConferencia = useMemo(() => paraConferir(lancamentos), [lancamentos]);
  /* O que o formulario completo le da pagina: a maquina escolhida, o
     abastecimento vizinho da data digitada, o piso do medidor e a faixa. */
  /* As ordens de abastecimento o servidor recusa a quem e de posto parceiro
     (0132): sem pedir, a tela nao recebe a recusa a cada entrada. */
  const consultaOrdens = useOrdens(sessaoAtual?.parceiro === undefined);
  const equipamentoDoFormulario = equipamentos.find((item) => item.codigo === codigoEscolhido) ?? null;
  const vizinhos = vizinhosDe(codigoEscolhido, abastecimentos, interpretarDataHora(quandoEscolhido));
  const ultimoRegistro = ultimoRegistroDeHorimetro(
    equipamentoDoFormulario?.ultimaLeitura ?? null,
    vizinhos.anterior,
    vizinhos.proximo === null,
  );
  const ordensAbertas = useMemo(
    () =>
      (consultaOrdens.data ?? []).filter((ordem) => ordem.situacao === 'aberta' && ordem.equipamentoId === (equipamentoDoFormulario?.id ?? '')),
    [consultaOrdens.data, equipamentoDoFormulario],
  );

  /* O medidor do abastecimento ANTERIOR da mesma máquina e do mesmo produto:
     é dele que sai o consumo que um lançamento novo implica, e é a mesma
     cadeia que o banco usa (por máquina E por produto, 0011). A lista já vem
     da mais recente para a mais antiga. */
  const medidorAnteriorDe = useCallback(
    (equipamentoId: string, produtoId: string) =>
      abastecimentos.find((a) => a.equipamentoId === equipamentoId && a.produtoId === produtoId && a.horimetro !== null)
        ?.horimetro ?? null,
    [abastecimentos],
  );
  /* A parte B lê os lançamentos JÁ cruzados com os equipamentos: enquanto
     qualquer das duas consultas não respondeu, os números dela não existem. */
  const carregandoLista = consultaAbastecimentos.isPending || consultaCadastros.isPending;
  const { precoMedio } = useMemo(() => gastoDosAbastecimentos(abastecimentos), [abastecimentos]);
  const consumoDoCartao =
    resumo.consumoMedio !== null
      ? { media: resumo.consumoMedio, unidade: 'L/h' }
      : resumo.consumoMedioPorKm !== null
        ? { media: resumo.consumoMedioPorKm, unidade: 'L/km' }
        : null;

  const filtrados = useMemo(
    () =>
      lancamentos.filter((lancamento) => {
        const temPendencia = lancamento.pendencias.length > 0;
        if (pendencia === 'Com pendência' && !temPendencia) return false;
        if (pendencia === 'Sem pendência' && temPendencia) return false;
        /* 0122 · "Para conferir" é mais estreito que "Com pendência": só o
           consumo fora da faixa que ninguém assinou. É o filtro do indicador
           de mesmo nome, e os dois leem a MESMA função — um card que abre uma
           lista com outra contagem é pior que card nenhum. */
        if (pendencia === 'Para conferir' && estadoDaVerificacao(lancamento) !== 'conferir') return false;

        const { abastecimento } = lancamento;
        /* A MESMA lista, na mesma ordem, que `CAMPOS_DA_BUSCA` anuncia no
           placeholder do campo. Campo pesquisado sem estar escrito lá produz
           linha que casa sem motivo visível. */
        return combinaComTermo(
          [
            abastecimento.equipamentoCodigo,
            lancamento.tipo,
            abastecimento.obra,
            abastecimento.operador,
            abastecimento.responsavel,
            abastecimento.centroCusto,
            abastecimento.tanque,
            abastecimento.posto ?? null,
          ],
          termo,
        );
      }),
    [lancamentos, pendencia, termo],
  );

  /* A lista da R06 (p.69): o corrigido com a mudança escrita; ligada a chave,
     os estornados voltam à posição original. Toda soma continua só dos que
     valem (`valem`, nas colunas). */
  const linhasDaLista = useMemo<readonly LinhaDoLancamento[]>(() => {
    const porId = new Map(estornados.map((e) => [e.id, e]));
    const vigentes = filtrados.map((l): LinhaDoLancamento => {
      if (!l.abastecimento.corrigeId) return l;
      return {
        ...l,
        correcao: textoDaCorrecaoDoAbastecimento(l.abastecimento, porId),
        motivoDaCorrecao: porId.get(l.abastecimento.corrigeId)?.estorno?.motivo ?? null,
      };
    });
    if (!mostrarEstornados) return vigentes;
    const anulados = estornados.map(
      (e): LinhaDoLancamento => ({ abastecimento: e, anterior: null, horas: null, consumo: null, unidade: 'L/h', tipo: null, faixa: null, pendencias: [], estornado: true }),
    );
    return juntarNaPosicaoOriginal(vigentes, anulados, (l) => l.abastecimento.quando);
  }, [filtrados, estornados, mostrarEstornados]);

  const paginas = Math.max(Math.ceil(linhasDaLista.length / porPagina), 1);
  const paginaSegura = Math.min(pagina, paginas);
  const inicio = (paginaSegura - 1) * porPagina;
  const visiveis = linhasDaLista.slice(inicio, inicio + porPagina);

  function irPara(estado: {
    readonly termo: string;
    readonly pendencia: FiltroDePendencia | undefined;
    readonly pagina: number;
    readonly porPagina: number;
    /** `true` para não encher o histórico do navegador a cada tecla. */
    readonly substituir?: boolean;
  }) {
    void navegar({
      search: montarBusca(estado),
      ...(estado.substituir ? { replace: true } : {}),
    });
  }

  function limparFiltros() {
    irPara({ termo: '', pendencia: undefined, pagina: 1, porPagina });
  }

  const chips: readonly ChipFiltro[] = [
    ...(pendencia
      ? [
          {
            id: 'pendencia',
            rotulo: `Pendência: ${pendencia}`,
            aoRemover: () => {
              irPara({ termo, pendencia: undefined, pagina: 1, porPagina });
            },
          },
        ]
      : []),
    ...(termo
      ? [
          {
            id: 'termo',
            rotulo: `Busca: ${termo}`,
            aoRemover: () => {
              irPara({ termo: '', pendencia, pagina: 1, porPagina });
            },
          },
        ]
      : []),
  ];

  /* Duas frases, e não uma com buraco. Concatenar um período ausente dentro de
     "{n} lançamentos, de {periodo}" produzia "0 lançamentos, de sem
     lançamentos" — português quebrado, e justamente no estado de primeiro uso,
     que é o estado do dia em que a empresa começa a usar o sistema. */
  const resumoDoTitulo =
    resumo.primeiro && resumo.ultimo
      ? `${formatarInteiro(resumo.quantidade)} lançamentos, de ${formatarData(resumo.primeiro)} a ${formatarData(resumo.ultimo)}`
      : 'Nenhum lançamento no período';

  /* A linha do painel sai da lista RECÉM-CHEGADA, pelo id: depois de conferir,
     a consulta é invalidada e a linha volta com a assinatura. Guardar o objeto
     deixaria o painel mostrando a versão de antes da própria ação dele. */
  const aberta = linhasDaLista.find((l) => l.abastecimento.id === linhaAberta) ?? null;

  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      <HubDoCombustivel>

      {/* PARTE B — visualizar o que foi lançado. */}
      <div className="flex flex-col gap-1">
        <h2 className="txt-secao text-tinta">Lançamentos do período</h2>
        {/* "Nenhum lançamento no período" antes da resposta afirmava o que
            ninguém sabia ainda (p.32): a linha é esqueleto. */}
        {carregandoLista ? (
          <Barra className="my-1 h-3 w-64 max-w-full" />
        ) : (
          <p className="txt-apoio text-tinta-secundaria">{resumoDoTitulo}</p>
        )}
      </div>

      {/* Só o que a faixa do hub não diz (p.17: "se ninguém pergunta aquilo de
          manhã, o indicador sai da tela"). Os litros saíram: eram o "Volume
          lançado" do hub com outro rótulo, o mesmo número duas vezes na tela.
          Nenhum traz variação: não há período anterior carregado para
          comparar, e um "0%" com cara de informação é pior que a ausência.
          Custo e consumo não são status: tom da marca (R02, p.31). */}
      <FaixaIndicadores>
        {/* O preço médio do litro, e não o custo: o custo do período é o
            "R$ … de gasto" do contexto do "Volume lançado" do hub, logo acima
            — o mesmo número duas vezes na tela (p.17). A média é ponderada
            SÓ pelos lançamentos que têm preço: com os litros de todos no
            denominador, os sem preço derrubavam a média. */}
        <CardIndicador
          rotulo="Preço médio"
          icone={CircleDollarSign}
          tom="marca"
          carregando={carregandoLista}
          valor={carregandoLista ? '' : precoMedio === null ? '—' : formatarReais(precoMedio)}
          {...(precoMedio === null ? {} : { unidade: '/L', unidadeColada: true })}
          contexto={carregandoLista ? '' : contextoDoPreco(precoMedio, resumo.semPreco)}
        />
        {/* L/h e L/km NUNCA na mesma conta. O valor é a média de horímetro;
            sem ela, a de odômetro. A outra, quando existe, vai escrita no
            contexto, com a unidade dela. */}
        <CardIndicador
          rotulo="Consumo médio"
          icone={Fuel}
          tom="marca"
          carregando={carregandoLista}
          valor={carregandoLista ? '' : consumoDoCartao === null ? '—' : formatarDecimal(consumoDoCartao.media, 1)}
          {...(consumoDoCartao === null ? {} : { unidade: consumoDoCartao.unidade })}
          contexto={carregandoLista ? '' : contextoDoConsumo(resumo)}
        />
        <CardIndicador
          rotulo="Com pendência"
          icone={Bell}
          tom="standby"
          carregando={carregandoLista}
          valor={carregandoLista ? '' : formatarInteiro(resumo.comPendencia)}
          {...(resumo.quantidade > 0 ? { proporcao: resumo.comPendencia / resumo.quantidade } : {})}
          contexto={carregandoLista ? '' : `de ${formatarInteiro(resumo.quantidade)} lançamentos no período`}
          aoAbrir={() => {
            irPara({ termo, pendencia: 'Com pendência', pagina: 1, porPagina });
          }}
        />
        {/* PARA CONFERIR (0122) — o quarto, e o único com ação de quem lê.
            Ele conta MENOS que "Com pendência" de propósito: só o consumo fora
            da faixa que ninguém assinou. Com tudo conferido, zera — e um
            indicador que ninguém consegue zerar é um indicador que todo mundo
            aprende a ignorar.
            VALOR EM PRETO, e o âmbar no bloco do ícone. O protótipo pinta o
            número; a regra 3 do manual (p.25) reserva a tinta no valor para
            quando ele é o PRÓPRIO alerta, e `alerta` aqui é o vermelho de
            "Parada". Fora da faixa é atenção, não erro: o lançamento entrou e
            o número pode estar certo. */}
        <CardIndicador
          rotulo="Para conferir"
          icone={Hourglass}
          tom="standby"
          carregando={carregandoLista}
          valor={carregandoLista ? '' : formatarInteiro(esperandoConferencia)}
          contexto={carregandoLista ? '' : 'consumo fora da faixa, sem conferência'}
          aoAbrir={() => {
            irPara({ termo, pendencia: 'Para conferir', pagina: 1, porPagina });
          }}
        />
      </FaixaIndicadores>

      {/* O CARTÃO DE LANÇAR, entre os números e a lista — onde o desenho da
          entrega (25/10/2026, tela 03) o põe, e onde ele serve: quem acabou de
          olhar "Para conferir" lança a próxima folha sem sair da tela.
          Dois modos, um lugar: a linha rápida de quatro perguntas e o
          formulário inteiro. Trocar de modo leva a máquina junto. */}
      {podeLancar && cadastros ? (
        modoDeLancar === 'rapido' ? (
          <LancamentoRapido
            equipamentos={equipamentos}
            tanques={cadastros.tanques}
            postos={cadastros.postos}
            produtos={cadastros.produtos}
            medidorAnteriorDe={medidorAnteriorDe}
            aoAbrirCompleto={(codigo) => {
              setCodigoEscolhido(codigo);
              setModoDeLancar('completo');
            }}
          />
        ) : (
          <div className="flex min-w-0 flex-col gap-3">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <h2 className="txt-secao text-tinta">Lançamento completo</h2>
              {/* A volta é SECUNDÁRIA e fica no topo, junto do título: o
                  laranja do cartão é o "Lançar abastecimento" do formulário. */}
              <Botao
                nivel="secundario"
                onClick={() => {
                  setModoDeLancar('rapido');
                }}
              >
                Voltar ao rápido
              </Botao>
            </div>
            {/* Sem o título do cartão: "Lançamento completo" já está logo acima. */}
            <FormularioAbastecimento
              titulo={null}
              cadastros={cadastros}
              ordensAbertas={ordensAbertas}
              codigoEscolhido={codigoEscolhido}
              aoEscolherEquipamento={setCodigoEscolhido}
              quando={quandoEscolhido}
              aoMudarQuando={setQuandoEscolhido}
              equipamento={equipamentoDoFormulario}
              anterior={vizinhos.anterior}
              proximo={vizinhos.proximo}
              ultimoRegistro={ultimoRegistro}
              faixa={equipamentoDoFormulario?.faixa ?? null}
            />
          </div>
        )
      ) : null}

      <BarraFiltros
        chips={chips}
        /* Vazia enquanto carrega: "0 de 0 lançamentos" é um número errado. */
        contagem={carregandoLista ? null : `${formatarInteiro(filtrados.length)} de ${formatarInteiro(lancamentos.length)} lançamentos`}
        aoLimpar={limparFiltros}
      >
        <div className="min-w-0 flex-1">
          <CampoTexto
            rotulo="Buscar"
            valor={termo}
            placeholder={CAMPOS_DA_BUSCA}
            atalho="ctrl-k"
            aplicado={Boolean(termo)}
            aoMudar={(novo) => {
              irPara({ termo: novo, pendencia, pagina: 1, porPagina, substituir: true });
            }}
          />
        </div>

        <CampoSelecao
          rotulo="Pendência"
          valor={pendencia ?? ''}
          rotuloVazio="Todos"
          aplicado={Boolean(pendencia)}
          opcoes={PENDENCIAS.map((nome) => ({ valor: nome, rotulo: nome }))}
          aoMudar={(novo) => {
            irPara({
              termo,
              pendencia: ehFiltroDePendencia(novo) ? novo : undefined,
              pagina: 1,
              porPagina,
            });
          }}
        />
      </BarraFiltros>

      {/* A chave dos estornados (R06, p.69), no cabeçalho da lista; só quando há estornado. */}
      {estornados.length > 0 ? (
        <div className="flex justify-end">
          <ChaveDeFiltro rotulo={`Mostrar estornados · ${formatarInteiro(estornados.length)}`} ligada={mostrarEstornados} aoMudar={setMostrarEstornados} />
        </div>
      ) : null}
      {/* A LISTA É A ORIGEM DO PAINEL: clicar em outra linha troca o detalhe
          em vez de fechá-lo (p.28). */}
      <div data-painel-origem>
      <Tabela
        rotulo="Lançamentos de abastecimento"
        colunas={COLUNAS}
        linhas={visiveis}
        alturaDupla
        chaveDaLinha={(lancamento) => lancamento.abastecimento.id}
        carregando={consultaAbastecimentos.isPending || consultaCadastros.isPending}
        rotuloDoTotal="Soma da página"
        selecionada={linhaAberta}
        aoSelecionar={(lancamento) => {
          setLinhaAberta(lancamento.abastecimento.id);
        }}
        /* Os tres pontos, fixos no canto direito (decisao 40): estornar e
           corrigir sao excecao, como no livro do tanque (0087). O menu e as
           janelas sao os da lista principal (`MenuDaLinhaDoAbastecimento`): a
           mesma linha oferece a mesma coisa nas duas telas. */
        menu={({ abastecimento, estornado }) => (
          <MenuDaLinhaDoAbastecimento
            abastecimento={abastecimento}
            estornado={estornado === true}
            aoEscolher={(escolha) => {
              setAcaoNaLinha(escolha);
            }}
          />
        )}
        erro={
          consultaAbastecimentos.isError ? (
            <EstadoVazio
              titulo="Não foi possível carregar os lançamentos"
              descricao="A lista não chegou. Tente carregar de novo; se continuar, avise o suporte com o horário em que aconteceu."
              acao={
                <Botao
                  nivel="secundario"
                  onClick={() => {
                    void consultaAbastecimentos.refetch();
                  }}
                >
                  Carregar os lançamentos
                </Botao>
              }
            />
          ) : null
        }
        vazio={
          lancamentos.length === 0 ? (
            /* Primeiro uso. Sem botão de ação de propósito: a ação seria
               "Lançar abastecimento", que já é o único botão laranja da tela
               e está no formulário logo acima. */
            <EstadoVazio
              titulo="Nenhum abastecimento lançado"
              descricao="Lance o primeiro abastecimento no formulário acima para começar a acompanhar o consumo da frota."
            />
          ) : (
            <EstadoVazio
              titulo="Nenhum lançamento com esses filtros"
              descricao="Troque a pendência, apague a busca ou limpe os filtros para ver o período inteiro."
              acao={
                <Botao nivel="secundario" onClick={limparFiltros}>
                  Limpar filtros
                </Botao>
              }
            />
          )
        }
        rodape={
          <RodapeTabela
            primeiro={inicio + 1}
            ultimo={inicio + visiveis.length}
            total={linhasDaLista.length}
            pagina={paginaSegura}
            paginas={paginas}
            porPagina={porPagina}
            opcoesPorPagina={OPCOES_POR_PAGINA}
            aoMudarPagina={(nova) => {
              irPara({ termo, pendencia, pagina: nova, porPagina });
            }}
            aoMudarPorPagina={(novo) => {
              irPara({ termo, pendencia, pagina: 1, porPagina: novo });
            }}
          />
        }
      />
      </div>

      <JanelasDoAbastecimento
        acao={acaoNaLinha?.acao ?? null}
        alvo={acaoNaLinha?.alvo ?? null}
        aoFechar={() => {
          setAcaoNaLinha(null);
        }}
        aoTrocarParaEstorno={() => {
          setAcaoNaLinha((atual) => (atual === null ? null : { acao: 'estorno', alvo: atual.alvo }));
        }}
      />
      </HubDoCombustivel>

      {aberta ? (
        <PainelDoLancamento
          key={aberta.abastecimento.id}
          linha={aberta}
          podeConferir={podeConferir}
          aoCorrigir={() => {
            setAcaoNaLinha({ acao: 'correcao', alvo: aberta.abastecimento });
          }}
          aoFechar={() => {
            setLinhaAberta(null);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * O PAINEL DO LANÇAMENTO (0122) — o detalhe, as verificações e a assinatura.
 *
 * Ele existe para uma pergunta: **este número está certo?** Por isso a aba que
 * abre é a das verificações, e não a do cadastro do lançamento. Quem abre o
 * painel está conferindo; quem quer saber o centro de custo abre a segunda aba.
 *
 * DUAS AÇÕES EM DESTAQUE, e o manual (p.20) permite duas: conferir e corrigir.
 * **Estornar continua nos três pontos da linha**, onde sempre esteve — ele pede
 * motivo e abre janela, e a janela sobre o painel seria a terceira camada
 * empilhada que a p.29 proíbe.
 *
 * AS DUAS SÃO SECUNDÁRIAS. O laranja desta tela é o "Lançar abastecimento" do
 * formulário, e a regra é uma ação laranja por tela.
 */
function PainelDoLancamento({
  linha,
  podeConferir,
  aoCorrigir,
  aoFechar,
}: {
  readonly linha: LinhaDoLancamento;
  readonly podeConferir: boolean;
  readonly aoCorrigir: () => void;
  readonly aoFechar: () => void;
}) {
  const conferir = useConferirAbastecimento();
  const { abastecimento, consumo, unidade, faixa } = linha;
  const estado = estadoDaVerificacao(linha);
  const verificacoes = verificacoesDoLancamento(linha);
  /* A barra só sai quando a faixa JULGA este lançamento: no ARLA ela desenharia
     a faixa do diesel com a marca do ARLA fora dela, a 20 px da linha que diz
     "não se aplica ao ARLA 32". */
  const barra = faixaJulgaOConsumo(linha) ? posicaoNaFaixa(consumo, faixa) : null;
  const medida = unidade === 'L/km' ? 'km' : 'h';
  const total = totalDoAbastecimento(abastecimento);
  const jaConferido = abastecimento.conferencia !== null && abastecimento.conferencia !== undefined;
  const estornado = linha.estornado === true;

  /* POR QUE as ações estão como estão, em texto visível (p.20): botão cinza
     com o motivo só no `title` não avisa ninguém — elemento `disabled` não
     recebe foco nem é lido pelo leitor de tela. */
  const nota = estornado
    ? 'Lançamento estornado não se confere nem se corrige. Confira o lançamento que entrou no lugar dele.'
    : jaConferido
      ? `Conferido${abastecimento.conferencia?.por ? ` por ${abastecimento.conferencia.por}` : ''} em ${formatarDataHora(abastecimento.conferencia?.em ?? abastecimento.quando)}. A conferência não se desfaz: o que muda um lançamento é a correção.`
      : podeConferir
        ? ''
        : 'Conferir pede a permissão “Conferir abastecimento” (abastecimento.conferir), que é outra que a de lançar. Peça a liberação a quem administra o sistema.';

  return (
    <PainelDetalhe
      codigo={abastecimento.equipamentoCodigo}
      titulo={linha.tipo ?? 'Fora da frota'}
      subtitulo={origemDaLinha(linha, formatarHora(abastecimento.quando))}
      marcadores={estornado ? undefined : <SeloDaVerificacao estado={estado} />}
      /* QUATRO dados-chave, e os quatro que se conferem: é o par litros e
         medidor que produz o consumo, e é o produto que diz se o consumo vale.
         Operador, obra e centro de custo estão na aba Lançamento — eles não
         entram nesta conta. */
      dadosChave={
        <>
          <DadoChave rotulo="Litros">
            <Medida valor={litrosEscritos(abastecimento.litros)} unidade="L" />
          </DadoChave>
          <DadoChave rotulo="Medidor">
            {abastecimento.horimetro === null ? (
              <span className="text-tinta-secundaria">Sem leitura</span>
            ) : (
              <Medida valor={formatarAteDuasCasas(abastecimento.horimetro)} unidade={medida} />
            )}
          </DadoChave>
          <DadoChave rotulo="Produto">{abastecimento.produto ?? 'Sem produto'}</DadoChave>
          <DadoChave rotulo="Quando">{formatarDataHora(abastecimento.quando)}</DadoChave>
        </>
      }
      abas={[
        {
          id: 'verificacoes',
          rotulo: 'Verificações',
          conteudo: (
            <div className="flex flex-col gap-4">
              {/* O CONSUMO CONTRA A FAIXA, desenhado. O número sozinho não diz
                  se 31,2 L/h é pouco ou muito fora; a barra diz de quanto.
                  Sem barra honesta a desenhar, o texto fica — e é o texto que
                  carrega o significado, nunca a figura. */}
              <div className="flex flex-col gap-2">
                {/* "por hora" só quando a máquina mede em hora. O caminhão mede
                    em quilômetro, e escrever "consumo por hora" sobre um número
                    em L/km é o defeito que esta tela já teve uma vez. */}
                <span className="txt-rotulo text-tinta-secundaria">{medida === 'km' ? 'Consumo por quilômetro' : 'Consumo por hora'}</span>
                {!faixaJulgaOConsumo(linha) ? (
                  /* ARLA e lubrificante não têm consumo por hora. O número existe
                     (o banco apura a cadeia de cada produto), mas ele não se
                     compara com nada — e mostrá-lo grande, sob este rótulo,
                     convida a comparar. */
                  <span className="txt-corpo text-tinta-secundaria">
                    {`Não se aplica a ${abastecimento.produto ?? 'este produto'}: a faixa é de litro de combustível por ${medida}.`}
                  </span>
                ) : consumo === null ? (
                  <span className="txt-corpo text-tinta-secundaria">
                    {abastecimento.limitacao ?? 'Ainda não apurado: o intervalo fecha no próximo abastecimento desta máquina.'}
                  </span>
                ) : (
                  <>
                    <span className="txt-kpi numero text-tinta">
                      <Medida valor={formatarDecimal(consumo, 1)} unidade={unidade} />
                    </span>
                    {barra === null ? null : (
                      <div className="flex flex-col gap-1">
                        <div className="relative h-2 w-full rounded-pill bg-rebaixada">
                          {/* A faixa esperada, em verde; a marca do lançamento
                              em cima dela — âmbar quando caiu fora. */}
                          <span
                            aria-hidden="true"
                            className="absolute inset-y-0 rounded-pill bg-sucesso-fundo"
                            style={{ left: `${barra.inicio}%`, width: `${barra.largura}%` }}
                          />
                          <span
                            aria-hidden="true"
                            className={`absolute -inset-y-1 w-1 rounded-pill ${estado === 'verificado' || jaConferido ? 'bg-sucesso' : 'bg-atencao'}`}
                            style={{ left: `calc(${barra.marca}% - 2px)` }}
                          />
                        </div>
                        <span className="txt-apoio text-tinta-secundaria">
                          {`Faixa da máquina: ${textoDaFaixa(faixa?.minimo ?? null, faixa?.maximo ?? null, unidade) ?? 'sem faixa cadastrada'}`}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <ul className="flex flex-col gap-3">
                {verificacoes.map((verificacao) => (
                  <LinhaDaVerificacao key={verificacao.titulo} verificacao={verificacao} />
                ))}
              </ul>
            </div>
          ),
        },
        {
          id: 'lancamento',
          rotulo: 'Lançamento',
          conteudo: (
            <div className="flex flex-col gap-3">
              <DadoChave rotulo="Origem">{origemDoLancamento(abastecimento)}</DadoChave>
              <DadoChave rotulo="Operador">{abastecimento.operador ?? 'Sem operador'}</DadoChave>
              <DadoChave rotulo="Liberou">{abastecimento.responsavel ?? 'Sem responsável'}</DadoChave>
              <DadoChave rotulo="Obra">{abastecimento.obra ?? 'Sem obra'}</DadoChave>
              <DadoChave rotulo="Centro de custo">{abastecimento.centroCusto ?? 'Sem centro de custo'}</DadoChave>
              <DadoChave rotulo="Custo">
                {total === null ? <span className="text-tinta-secundaria">Sem preço</span> : formatarReais(total)}
              </DadoChave>
              {abastecimento.comprovante ? <DadoChave rotulo="Comprovante">{abastecimento.comprovante}</DadoChave> : null}
              {abastecimento.ids?.observacao ? (
                <div className="flex flex-col gap-1">
                  <span className="txt-rotulo text-tinta-secundaria">Observação</span>
                  <p className="txt-corpo text-pretty text-tinta">{abastecimento.ids.observacao}</p>
                </div>
              ) : null}
            </div>
          ),
        },
      ]}
      acoes={[
        <Botao
          key="conferir"
          nivel="secundario"
          disabled={jaConferido || estornado || !podeConferir || conferir.isPending}
          onClick={() => {
            conferir.mutate({
              id: abastecimento.id,
              contexto: {
                alvo: { codigo: abastecimento.equipamentoCodigo },
                fatos: [`${litrosEscritos(abastecimento.litros)} L de ${abastecimento.produto ?? 'combustível'}`],
              },
            });
          }}
        >
          {jaConferido ? 'Conferido' : 'Marcar como conferido'}
        </Botao>,
        <Botao key="corrigir" nivel="secundario" disabled={estornado} onClick={aoCorrigir}>
          Corrigir lançamento
        </Botao>,
      ]}
      {...(nota ? { notaDasAcoes: nota } : {})}
      aoFechar={aoFechar}
    />
  );
}

/** O ícone de cada nível, só do vocabulário do manual (p.19). */
const ICONE_DA_VERIFICACAO: Readonly<Record<NivelDaVerificacao, { readonly icone: typeof CircleCheck; readonly cor: string }>> = {
  ok: { icone: CircleCheck, cor: 'text-sucesso' },
  atencao: { icone: TriangleAlert, cor: 'text-atencao' },
  erro: { icone: CircleX, cor: 'text-erro' },
  /* "Aguardando" do complemento mobile: ainda não há como saber. */
  pendente: { icone: Hourglass, cor: 'text-tinta-secundaria' },
  /* "Informação": não se aplica a este lançamento — e isso não é um problema. */
  naoSeAplica: { icone: Info, cor: 'text-tinta-secundaria' },
};

function LinhaDaVerificacao({ verificacao }: { readonly verificacao: Verificacao }) {
  const { icone: Icone, cor } = ICONE_DA_VERIFICACAO[verificacao.nivel];
  return (
    <li className="flex items-start gap-2">
      <Icone aria-hidden="true" className={`mt-0.5 size-[var(--icone-controle)] shrink-0 ${cor}`} />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="txt-corpo text-pretty text-tinta">{verificacao.titulo}</span>
        <span className="txt-apoio text-pretty text-tinta-secundaria">{verificacao.detalhe}</span>
      </span>
    </li>
  );
}

/**
 * O contexto do consumo médio diz sobre QUAIS lançamentos a conta foi feita —
 * e, quando há das duas unidades, a outra média, que não entra no número.
 */
function contextoDoConsumo(resumo: ResumoDoPeriodo): string {
  const quantidade = formatarInteiro(resumo.quantidade);
  if (resumo.consumoMedio !== null && resumo.consumoMedioPorKm !== null) {
    return `${formatarInteiro(resumo.comBaseDeConsumo)} de ${quantidade} lançamentos, só horímetro; com odômetro, ${formatarDecimal(resumo.consumoMedioPorKm, 1)} L/km`;
  }
  if (resumo.consumoMedio !== null) {
    return `${formatarInteiro(resumo.comBaseDeConsumo)} de ${quantidade} lançamentos têm leitura anterior`;
  }
  if (resumo.consumoMedioPorKm !== null) {
    return `${formatarInteiro(resumo.comBaseDeConsumoPorKm)} de ${quantidade} lançamentos têm leitura anterior, todos de odômetro`;
  }
  return 'nenhum lançamento tem leitura anterior para comparar';
}

/** O contexto do preço médio diz sobre o que a média foi feita, e quantos lançamentos ficaram FORA dela. */
function contextoDoPreco(precoMedio: number | null, semPreco: number): string {
  if (precoMedio === null) return 'nenhum lançamento do período tem preço';
  if (semPreco > 0) {
    return `média ponderada; ${formatarInteiro(semPreco)} ${semPreco === 1 ? 'lançamento entrou' : 'lançamentos entraram'} sem preço e ${semPreco === 1 ? 'fica' : 'ficam'} de fora`;
  }
  return 'média ponderada pelos litros de todos os lançamentos';
}
